import React, { useState } from "react";
import { Building2, Clock, Loader2, Mail, ShieldCheck } from "lucide-react";

import {
  useAcademies,
  useAcceptAcademyInvitation,
  useDeclineAcademyInvitation,
  useJoinAcademy,
  useLeaveAcademy,
  useMyAcademyMembership,
} from "../../hooks/queries/useAcademies";

/**
 * "Mi academia": el atleta pide unirse a su academia y un entrenador verificado
 * de esa academia lo confirma — o la academia lo invita y él acepta o rechaza.
 * Confirmado, aparece en "Mis deportistas" del coach y sus entrenadores pueden
 * abrir su perfil y sus reportes.
 */
const AcademyMembershipCard = () => {
  const membershipQuery = useMyAcademyMembership();
  const membership = membershipQuery.data;
  const academiesQuery = useAcademies({ enabled: membershipQuery.isSuccess && !membership });
  const join = useJoinAcademy();
  const leave = useLeaveAcademy();
  const accept = useAcceptAcademyInvitation();
  const decline = useDeclineAcademyInvitation();
  const [academyId, setAcademyId] = useState("");
  const [confirmingLeave, setConfirmingLeave] = useState(false);

  const error = join.error || leave.error || accept.error || decline.error;
  const busy = join.isPending || leave.isPending || accept.isPending || decline.isPending;
  const invited = membership?.status === "pending" && membership?.requested_by === "academy";

  const handleLeave = () => {
    if (membership?.status === "confirmed" && !confirmingLeave) {
      setConfirmingLeave(true);
      return;
    }
    setConfirmingLeave(false);
    leave.mutate();
  };

  if (membershipQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 p-3 text-sm text-gray-500">
        <Loader2 size={16} className="animate-spin" aria-hidden="true" />
        Cargando tu academia…
      </div>
    );
  }

  if (membershipQuery.isError) {
    return (
      <p role="alert" className="p-3 text-sm text-red-600">
        No se pudo cargar tu academia. {membershipQuery.error.message}
      </p>
    );
  }

  return (
    <div className="space-y-3 rounded-lg p-3">
      {invited ? (
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-blue-100 p-2 text-blue-700">
            <Mail size={18} aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-gray-900">{membership.academy_name}</p>
            <p className="text-xs text-gray-500">
              Te invitó a unirte a su academia. Si aceptas, sus entrenadores podrán ver tu perfil, tus datos de contacto
              y tus reportes.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => accept.mutate(membership.id)}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
              >
                {accept.isPending && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
                Aceptar
              </button>
              <button
                type="button"
                onClick={() => decline.mutate(membership.id)}
                disabled={busy}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Rechazar
              </button>
              {decline.isPending && <Loader2 size={14} className="animate-spin text-blue-900" aria-hidden="true" />}
            </div>
          </div>
        </div>
      ) : membership ? (
        <div className="flex items-start gap-3">
          <div
            className={`rounded-lg p-2 ${
              membership.status === "confirmed" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
            }`}
          >
            {membership.status === "confirmed" ? (
              <ShieldCheck size={18} aria-hidden="true" />
            ) : (
              <Clock size={18} aria-hidden="true" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-gray-900">{membership.academy_name}</p>
            <p className="text-xs text-gray-500">
              {membership.status === "confirmed"
                ? "Eres parte de esta academia. Tus entrenadores pueden ver tu perfil y tus reportes."
                : "Solicitud enviada. Un entrenador de la academia debe confirmarla; te avisaremos cuando lo haga."}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleLeave}
                disabled={busy}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                {membership.status === "confirmed"
                  ? confirmingLeave
                    ? "Sí, salir de la academia"
                    : "Salir de la academia"
                  : "Cancelar solicitud"}
              </button>
              {confirmingLeave && (
                <button
                  type="button"
                  onClick={() => setConfirmingLeave(false)}
                  className="px-2 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700"
                >
                  No, quedarme
                </button>
              )}
              {leave.isPending && <Loader2 size={14} className="animate-spin text-blue-900" aria-hidden="true" />}
            </div>
          </div>
        </div>
      ) : (
        <form
          className="flex items-start gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (academyId) join.mutate(academyId);
          }}
        >
          <div className="rounded-lg bg-blue-100 p-2 text-blue-700">
            <Building2 size={18} aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <label htmlFor="academy-select" className="text-sm font-bold text-gray-900">
              Únete a tu academia
            </label>
            <p className="text-xs text-gray-500">
              Elige tu club o academia. Cuando un entrenador confirme tu ingreso, podrá ver tu perfil y tus reportes.
            </p>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <select
                id="academy-select"
                value={academyId}
                onChange={(event) => setAcademyId(event.target.value)}
                disabled={academiesQuery.isLoading || busy}
                className="min-h-10 min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-900 focus:outline-none"
              >
                <option value="">
                  {academiesQuery.isLoading ? "Cargando academias…" : "Selecciona una academia"}
                </option>
                {(academiesQuery.data || []).map((academy) => (
                  <option key={academy.id} value={academy.id}>
                    {academy.name}
                    {academy.city ? ` · ${academy.city}` : ""}
                  </option>
                ))}
              </select>
              <button
                type="submit"
                disabled={!academyId || busy}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-blue-900 px-4 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
              >
                {join.isPending && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
                Solicitar ingreso
              </button>
            </div>
            {academiesQuery.isSuccess && academiesQuery.data.length === 0 && (
              <p className="mt-1 text-xs text-gray-500">Todavía no hay academias registradas.</p>
            )}
          </div>
        </form>
      )}
      {error && (
        <p role="alert" className="text-xs text-red-600">
          {error.message}
        </p>
      )}
    </div>
  );
};

export default AcademyMembershipCard;
